import React, { useState, useEffect } from 'react';
import CountryFlag from '../../../shared/components/CountryFlag';
import {
    Table,
    Button,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    Select,
    message,
    Card,
    Drawer,
    Popconfirm,
    Tooltip,
    InputNumber,
    Descriptions,
    Badge,
    Switch,
    Tabs
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    EditOutlined,
    CloudServerOutlined,
    ApiOutlined,
    SyncOutlined,
    SettingOutlined,
    KeyOutlined,
    RocketOutlined,
    ControlOutlined,
    AlertOutlined
} from '@ant-design/icons';
import evmAdminService from '../../../shared/services/evmAdminService';
import ElasticConfigDrawer from './ElasticConfigDrawer';
import NodeIpv6UplinkDrawer from './NodeIpv6UplinkDrawer';
import NodeHaModal from './NodeHaModal';
import NodeDeployModal from './NodeDeployModal';
import VdsNodePanel from './VdsNodePanel';
import { useSearchParams } from 'react-router-dom';
import { t } from '../../../shared/i18n';

const { Option } = Select;

const NodeManagement = () => {
    const [nodes, setNodes] = useState([]);
    const [areas, setAreas] = useState([]);
    const [zones, setZones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [createDrawerVisible, setCreateDrawerVisible] = useState(false);
    const [editDrawerVisible, setEditDrawerVisible] = useState(false);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [currentNode, setCurrentNode] = useState(null);
    const [detailTab, setDetailTab] = useState('info');
    const [searchParams] = useSearchParams();
    const [testingNode, setTestingNode] = useState(null);
    const [deployNode, setDeployNode] = useState(null);
    const [elasticNode, setElasticNode] = useState(null); // {id, name}; id 0 = global
    const [v6Node, setV6Node] = useState(null);
    const [haNode, setHaNode] = useState(null);
    const [haDead, setHaDead] = useState({}); // node_id -> true（列表接口不带 ha_state，停用节点单独查）

    const [createForm] = Form.useForm();
    const [editForm] = Form.useForm();

    useEffect(() => {
        loadNodes();
        loadAreas();
    }, []);

    const loadHaStates = async (list) => {
        const disabled = list.filter((n) => n.status === 'disable' && n.ha_state === undefined);
        const res = await Promise.all(disabled.map((n) => evmAdminService.getNodeHa(n.id)));
        const map = {};
        list.forEach((n) => { if (n.ha_state === 'dead') map[n.id] = true; });
        res.forEach((r, i) => { if (r?.data?.node?.ha_state === 'dead') map[disabled[i].id] = true; });
        setHaDead(map);
    };

    const loadNodes = async () => {
        setLoading(true);
        try {
            const response = await evmAdminService.getNodeList();
            if (response.status === 'success') {
                const nodesData = Array.isArray(response.data)
                    ? response.data
                    : (response.data?.data || response.data?.nodes || []);
                setNodes(nodesData);
                loadHaStates(nodesData);
            } else {
                message.error(response.msg || 'Failed to load nodes');
            }
        } catch (error) {
            message.error('Failed to load nodes');
        } finally {
            setLoading(false);
        }
    };

    const loadAreas = async () => {
        try {
            const response = await evmAdminService.getAreas();
            if (response.status === 'success') {
                const areasData = Array.isArray(response.data)
                    ? response.data
                    : (response.data?.data || response.data?.areas || []);
                setAreas(areasData);
            }
        } catch (error) {
            console.error('Failed to load areas', error);
        }
    };

    const loadZonesByArea = async (areaId) => {
        try {
            const response = await evmAdminService.getZonesByArea(areaId);
            if (response.status === 'success') {
                const zonesData = Array.isArray(response.data)
                    ? response.data
                    : (response.data?.data || response.data?.zones || []);
                setZones(zonesData);
            }
        } catch (error) {
            console.error('Failed to load zones', error);
        }
    };

    const handleTestConnection = async (node) => {
        setTestingNode(node.id);
        try {
            const response = await evmAdminService.testNodeConnection(node.id);
            if (response.status === 'success') {
                message.success(`Connection successful! Hostname: ${response.data?.hostname}`);
            } else {
                message.error(response.msg || 'Connection failed');
            }
        } catch (error) {
            message.error('Connection test failed');
        } finally {
            setTestingNode(null);
        }
    };

    // 环境检测与部署都在一键部署弹窗里(后台任务 + 实时日志)
    const handleDeployNode = (node) => setDeployNode(node);

    const handleRegenerateCredentials = async (node) => {
        try {
            const response = await evmAdminService.regenerateNodeCredentials(node.id);
            if (response.status === 'success') {
                Modal.success({
                    title: 'New Credentials',
                    content: (
                        <div>
                            <p><strong>Token:</strong> {response.data?.token}</p>
                            <p><strong>Key:</strong> {response.data?.key}</p>
                        </div>
                    )
                });
            } else {
                message.error(response.msg || 'Failed to regenerate credentials');
            }
        } catch (error) {
            message.error('Failed to regenerate credentials');
        }
    };

    const handleDelete = async (node) => {
        try {
            const response = await evmAdminService.deleteNode(node.id);
            if (response.status === 'success') {
                message.success('Node deleted');
                loadNodes();
            } else {
                message.error(response.msg || 'Delete failed');
            }
        } catch (error) {
            message.error('Delete failed');
        }
    };

    const showCreateDrawer = () => {
        createForm.resetFields();
        setCreateDrawerVisible(true);
    };

    const showEditDrawer = (node) => {
        setCurrentNode(node);
        editForm.setFieldsValue({
            ...node,
            dhcp_enabled: node.dhcp_enabled ?? true,
            ndppd_enabled: node.ndppd_enabled ?? false
        });
        if (node.area_id) {
            loadZonesByArea(node.area_id);
        }
        setEditDrawerVisible(true);
    };

    // ?vds=<id> (from the VDS page) opens that node's VDS tab
    useEffect(() => {
        const id = Number(searchParams.get('vds'));
        if (id) showDetailModal({ id }, 'vds');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showDetailModal = async (node, tab = 'info') => {
        setDetailTab(tab);
        try {
            const response = await evmAdminService.getNode(node.id);
            if (response.status === 'success') {
                setCurrentNode(response.data?.node || node);
            } else {
                setCurrentNode(node);
            }
        } catch (error) {
            setCurrentNode(node);
        }
        setDetailModalVisible(true);
    };

    const handleCreate = async (values) => {
        try {
            const response = await evmAdminService.createNode(values);
            if (response.status === 'success') {
                message.success('Node created');
                Modal.success({
                    title: 'Node Credentials',
                    content: (
                        <div>
                            <p><strong>Token:</strong> {response.data?.token}</p>
                            <p><strong>Key:</strong> {response.data?.key}</p>
                            <p style={{ color: '#ff4d4f' }}>Please save these credentials securely!</p>
                        </div>
                    )
                });
                setCreateDrawerVisible(false);
                loadNodes();
            } else {
                message.error(response.msg || 'Failed to create node');
            }
        } catch (error) {
            message.error('Failed to create node');
        }
    };

    const handleUpdate = async (values) => {
        try {
            const response = await evmAdminService.updateNode(currentNode.id, values);
            if (response.status === 'success') {
                message.success('Node updated');
                setEditDrawerVisible(false);
                loadNodes();
            } else {
                message.error(response.msg || 'Failed to update node');
            }
        } catch (error) {
            message.error('Failed to update node');
        }
    };

    const getStatusTag = (status) => {
        const statusMap = {
            'enable': { color: 'green', text: 'Enabled' },
            'disable': { color: 'red', text: 'Disabled' },
            'check': { color: 'orange', text: 'Checking' },
            'deploy': { color: 'blue', text: 'Deploying' },
            'error': { color: 'red', text: 'Error' }
        };
        const config = statusMap[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 150,
            render: (text, record) => (
                <a onClick={() => showDetailModal(record)}>{text}</a>
            )
        },
        {
            title: 'IP Address',
            dataIndex: 'ipaddress',
            key: 'ipaddress',
            width: 140
        },
        {
            title: 'Zone',
            dataIndex: 'zone_id',
            key: 'zone_id',
            width: 80,
            render: (v, r) => <><CountryFlag code={areas.find((a) => Number(a.id) === Number(r.area_id))?.country} />{v}</>
        },
        {
            title: 'Network Type',
            dataIndex: 'network_type',
            key: 'network_type',
            width: 130,
            render: (type) => <Tag>{type || 'ovs_routed'}</Tag>
        },
        {
            title: 'Bridge Mode',
            dataIndex: 'bridge_mode',
            key: 'bridge_mode',
            width: 100
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status, record) => (
                <Space size={4}>
                    {getStatusTag(status)}
                    {haDead[record.id] && <Tag color="error">已标记故障</Tag>}
                </Space>
            )
        },
        {
            title: 'Actions',
            key: 'action',
            fixed: 'right',
            width: 350,
            render: (_, record) => (
                <Space size="small" wrap>
                    <Tooltip title="Test Connection">
                        <Button
                            size="small"
                            icon={<ApiOutlined />}
                            loading={testingNode === record.id}
                            onClick={() => handleTestConnection(record)}
                        />
                    </Tooltip>
                    <Tooltip title={t('One-click deploy')}>
                        <Button
                            size="small"
                            type="primary"
                            icon={<RocketOutlined />}
                            onClick={() => handleDeployNode(record)}
                        />
                    </Tooltip>
                    <Tooltip title="弹性配置">
                        <Button size="small" icon={<ControlOutlined />} onClick={() => setElasticNode({ id: record.id, name: record.name })} />
                    </Tooltip>
                    <Tooltip title={haDead[record.id] ? '恢复节点' : '故障转移'}>
                        <Button size="small" danger={!haDead[record.id]} icon={<AlertOutlined />} onClick={() => setHaNode(record)} />
                    </Tooltip>
                    <Tooltip title="IPv6 上联">
                        <Button size="small" onClick={() => setV6Node(record)}>v6</Button>
                    </Tooltip>
                    <Tooltip title="Edit">
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => showEditDrawer(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Regenerate Credentials">
                        <Popconfirm
                            title="Regenerate credentials?"
                            onConfirm={() => handleRegenerateCredentials(record)}
                        >
                            <Button size="small" icon={<KeyOutlined />} />
                        </Popconfirm>
                    </Tooltip>
                    <Popconfirm
                        title="Delete this node?"
                        onConfirm={() => handleDelete(record)}
                    >
                        <Tooltip title="Delete">
                            <Button size="small" danger icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const NodeForm = ({ form, onFinish, initialValues }) => (
        <Form form={form} layout="vertical" onFinish={onFinish} initialValues={initialValues}>
            <Form.Item
                name="name"
                label="Node Name"
                rules={[{ required: true, message: 'Please enter node name' }]}
            >
                <Input placeholder="e.g., node-us-1" />
            </Form.Item>

            <Form.Item
                name="ipaddress"
                label="IP Address"
                rules={[{ required: true, message: 'Please enter IP address' }]}
            >
                <Input placeholder="e.g., 192.168.1.100" />
            </Form.Item>

            <Form.Item name="ssh_port" label="SSH Port" initialValue={22}>
                <InputNumber min={1} max={65535} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item name="ssh_user" label="SSH User" initialValue="root">
                <Input />
            </Form.Item>

            <Form.Item
                name="ssh_passwd"
                label="SSH Password"
                rules={[{ required: !initialValues, message: 'Please enter SSH password' }]}
            >
                <Input.Password placeholder="SSH password" />
            </Form.Item>

            <Form.Item name="area_id" label="Area">
                <Select
                    placeholder="Select area"
                    onChange={(value) => loadZonesByArea(value)}
                    allowClear
                >
                    {areas.map(area => (
                        <Option key={area.id} value={area.id}><CountryFlag code={area.country} />{area.name}</Option>
                    ))}
                </Select>
            </Form.Item>

            <Form.Item name="zone_id" label="Zone">
                <Select placeholder="Select zone" allowClear>
                    {zones.map(zone => (
                        <Option key={zone.id} value={zone.id}>{zone.name} ({zone.code})</Option>
                    ))}
                </Select>
            </Form.Item>

            <Form.Item name="bridge_mode" label="Bridge Mode" initialValue="ovs">
                <Select>
                    <Option value="ovs">OVS</Option>
                    <Option value="linux">Linux Bridge</Option>
                </Select>
            </Form.Item>

            <Form.Item name="network_type" label="Network Type" initialValue="ovs_routed">
                <Select>
                    <Option value="ovs_routed">OVS Routed</Option>
                    <Option value="ovs_bridge">OVS Bridge</Option>
                    <Option value="linux_bridge">Linux Bridge</Option>
                    <Option value="libvirt_routed">Libvirt Routed</Option>
                    <Option value="libvirt_nat">Libvirt NAT</Option>
                    <Option value="macvtap">MacVTap</Option>
                </Select>
            </Form.Item>

            <Form.Item name="filter_mode" label="Filter Mode" initialValue="native_performance_v6">
                <Select>
                    <Option value="none">None</Option>
                    <Option value="libvirt">Libvirt</Option>
                    <Option value="native_standard">Native Standard</Option>
                    <Option value="native_performance">Native Performance</Option>
                    <Option value="native_performance_v6">Native Performance v6</Option>
                </Select>
            </Form.Item>

            <Form.Item name="physical_nic" label="Physical NIC">
                <Input placeholder="e.g., eth0, ens3" />
            </Form.Item>

            <Form.Item name="vxlan_ip" label="VXLAN IP (VTEP)">
                <Input placeholder="IP for VXLAN tunnels" />
            </Form.Item>

            <Form.Item name="dhcp_enabled" label="DHCP Enabled" valuePropName="checked">
                <Switch />
            </Form.Item>

            <Form.Item name="dhcp_service" label="DHCP Service" initialValue="dnsmasq">
                <Select>
                    <Option value="dnsmasq">dnsmasq</Option>
                    <Option value="isc-dhcp">ISC DHCP</Option>
                    <Option value="none">None</Option>
                </Select>
            </Form.Item>

            <Form.Item name="ndppd_enabled" label="NDPPD Enabled" valuePropName="checked">
                <Switch />
            </Form.Item>

            <Form.Item name="status" label="Status" initialValue="check">
                <Select>
                    <Option value="check">Check</Option>
                    <Option value="enable">Enable</Option>
                    <Option value="disable">Disable</Option>
                </Select>
            </Form.Item>

            <Form.Item name="sort" label="Sort Order" initialValue={0}>
                <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
        </Form>
    );

    return (
        <div>
            <Card
                title={<span><CloudServerOutlined /> 节点管理</span>}
                extra={
                    <Space>
                        <Button type="primary" icon={<PlusOutlined />} onClick={showCreateDrawer}>
                            Add Node
                        </Button>
                        <Button icon={<ControlOutlined />} onClick={() => setElasticNode({ id: 0 })}>
                            弹性配置
                        </Button>
                        <Button icon={<SyncOutlined />} onClick={loadNodes}>
                            Refresh
                        </Button>
                    </Space>
                }
            >
                <Table
                    columns={columns}
                    dataSource={nodes}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: 1200 }}
                    pagination={{ pageSize: 20 }}
                />
            </Card>

            {/* Create Node Drawer */}
            <Drawer
                title="添加节点"
                width={500}
                open={createDrawerVisible}
                onClose={() => setCreateDrawerVisible(false)}
                extra={
                    <Space>
                        <Button onClick={() => setCreateDrawerVisible(false)}>Cancel</Button>
                        <Button type="primary" onClick={() => createForm.submit()}>Create</Button>
                    </Space>
                }
            >
                <NodeForm form={createForm} onFinish={handleCreate} />
            </Drawer>

            {/* Edit Node Drawer */}
            <Drawer
                title="编辑节点"
                width={500}
                open={editDrawerVisible}
                onClose={() => setEditDrawerVisible(false)}
                extra={
                    <Space>
                        <Button onClick={() => setEditDrawerVisible(false)}>Cancel</Button>
                        <Button type="primary" onClick={() => editForm.submit()}>Save</Button>
                    </Space>
                }
            >
                <NodeForm form={editForm} onFinish={handleUpdate} initialValues={currentNode} />
            </Drawer>

            <ElasticConfigDrawer
                open={!!elasticNode}
                nodeId={elasticNode?.id || 0}
                nodeName={elasticNode?.name}
                onClose={() => setElasticNode(null)}
            />

            {/* Node Detail Modal */}
            <Modal
                title="节点详情"
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={null}
                width={detailTab === 'vds' ? 1000 : 700}
            >
                {currentNode && (
                    <Tabs activeKey={detailTab} onChange={setDetailTab} items={[{ key: 'info', label: t('Details'), children: (
                    <Descriptions bordered column={2}>
                        <Descriptions.Item label="ID">{currentNode.id}</Descriptions.Item>
                        <Descriptions.Item label="Name">{currentNode.name}</Descriptions.Item>
                        <Descriptions.Item label="IP Address">{currentNode.ipaddress}</Descriptions.Item>
                        <Descriptions.Item label="Status">{getStatusTag(currentNode.status)}</Descriptions.Item>
                        <Descriptions.Item label="Area ID">{currentNode.area_id}</Descriptions.Item>
                        <Descriptions.Item label="Zone ID">{currentNode.zone_id}</Descriptions.Item>
                        <Descriptions.Item label="Bridge Mode">{currentNode.bridge_mode}</Descriptions.Item>
                        <Descriptions.Item label="Network Type">{currentNode.network_type}</Descriptions.Item>
                        <Descriptions.Item label="Filter Mode">{currentNode.filter_mode}</Descriptions.Item>
                        <Descriptions.Item label="Physical NIC">{currentNode.physical_nic}</Descriptions.Item>
                        <Descriptions.Item label="VXLAN IP">{currentNode.vxlan_ip}</Descriptions.Item>
                        <Descriptions.Item label="DHCP Enabled">
                            <Badge status={currentNode.dhcp_enabled ? 'success' : 'default'} text={currentNode.dhcp_enabled ? 'Yes' : 'No'} />
                        </Descriptions.Item>
                        <Descriptions.Item label="DHCP Service">{currentNode.dhcp_service}</Descriptions.Item>
                        <Descriptions.Item label="NDPPD Enabled">
                            <Badge status={currentNode.ndppd_enabled ? 'success' : 'default'} text={currentNode.ndppd_enabled ? 'Yes' : 'No'} />
                        </Descriptions.Item>
                        <Descriptions.Item label="SSH Port">{currentNode.ssh_port || 22}</Descriptions.Item>
                        <Descriptions.Item label="SSH User">{currentNode.ssh_user || 'root'}</Descriptions.Item>
                        <Descriptions.Item label="Created At" span={2}>{currentNode.created_at}</Descriptions.Item>
                    </Descriptions>
                    ) }, { key: 'vds', label: 'VDS', children: detailTab === 'vds' ? <VdsNodePanel nodeId={currentNode.id} /> : null }]} />
                )}
            </Modal>
            <NodeIpv6UplinkDrawer node={v6Node} onClose={() => setV6Node(null)} />
            <NodeHaModal node={haNode} nodes={nodes} onClose={() => setHaNode(null)} onChanged={loadNodes} />
            <NodeDeployModal node={deployNode} onClose={() => setDeployNode(null)} onDone={loadNodes} />
        </div>
    );
};

export default NodeManagement;
